import { useEffect, useState } from "react";
import api, { API_BASE, openPdf, postDownload } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import CustomerAutocomplete from "../components/CustomerAutocomplete";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import FilterPills, { useQueryFilter } from "../components/FilterPills";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Plus, Trash2, FileText, Truck, Receipt, FileSignature, PackageCheck, Sticker, Package, Send, CheckSquare, Square, UserPlus, PackagePlus, Layers, FileUp, Search, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import PhotoAttachments from "../components/PhotoAttachments";
import ImportDialog from "../components/ImportDialog";
import ItemCombobox from "../components/ItemCombobox";
import BatchCombobox from "../components/BatchCombobox";

export default function Deliveries() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupForm, setPickupForm] = useState({ pickup_date: "", time_window: "09:00-12:00", package_count: 1, notes: "" });

  const submitPickup = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/dsv/pickups", pickupForm);
      toast.success(`DSV-Abholung gebucht · ${data.pickup_id}${data.sandbox ? " (Sandbox)" : ""}`);
      setPickupOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Abholung fehlgeschlagen"); }
  };
  const [form, setForm] = useState({ customer_id: "", delivery_date: "", tour: "", notes: "", shipping_cost: 0, payment_terms_days: 14, pallets_out: 0, pallets_returned: 0 });
  const [lines, setLines] = useState([{ item_id: "", batch_id: "", quantity: "", bundle_code: "" }]);
  const [palletCfg, setPalletCfg] = useState({ default_exchange: true, threshold: 600 });
  const [detail, setDetail] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invEdit, setInvEdit] = useState({ payment_terms_days: 14, payment_status: "open", shipping_cost: 0, invoice_date: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState({});
  const [q, setQ] = useQueryFilter("q", "");
  const [custFilter, setCustFilter] = useQueryFilter("kunde", "all");

  // Inline "neuer Kunde" beim Lieferschein-Erstellen
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({
    name: "", type: "B2B", email: "", phone: "",
    street: "", zip: "", city: "", country: "DE",
    payment_terms_days: 14,
  });
  const [savingCust, setSavingCust] = useState(false);

  const openNewCust = () => {
    setNewCust({
      name: "", type: "B2B", email: "", phone: "",
      street: "", zip: "", city: "", country: "DE",
      payment_terms_days: 14,
    });
    setNewCustOpen(true);
  };

  const saveNewCust = async (e) => {
    e.preventDefault();
    if (!newCust.name.trim()) return toast.error("Name ist Pflicht");
    setSavingCust(true);
    try {
      const payload = { ...newCust, email: newCust.email || null };
      const { data } = await api.post("/customers", payload);
      toast.success(`Kunde "${data.name}" angelegt`);
      // Refresh list + auto-select the new customer
      const list = (await api.get("/customers")).data;
      setCustomers(list);
      setForm((f) => ({ ...f, customer_id: data.id }));
      setNewCustOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde konnte nicht angelegt werden");
    } finally {
      setSavingCust(false);
    }
  };

  // Inline "neuer Artikel" pro Positions-Zeile
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newItemLineIdx, setNewItemLineIdx] = useState(null);
  const [newItem, setNewItem] = useState({
    sku: "", name: "", unit: "stk", barcode: "",
    price: "", vat_rate: 0.07, default_shelf_life_days: 180,
  });
  const [savingItem, setSavingItem] = useState(false);

  const openNewItem = (idx, prefillName = "") => {
    setNewItemLineIdx(idx);
    setNewItem({ sku: "", name: prefillName || "", unit: "stk", barcode: "",
                 price: "", vat_rate: 0.07, default_shelf_life_days: 180 });
    setNewItemOpen(true);
  };

  const saveNewItem = async (e) => {
    e.preventDefault();
    if (!newItem.sku.trim() || !newItem.name.trim()) return toast.error("SKU + Name sind Pflicht");
    setSavingItem(true);
    try {
      const payload = {
        sku: newItem.sku.trim(),
        name: newItem.name.trim(),
        type: "finished",
        unit: newItem.unit,
        barcode: newItem.barcode || null,
        price: newItem.price === "" ? null : Number(newItem.price),
        vat_rate: Number(newItem.vat_rate),
        default_shelf_life_days: Number(newItem.default_shelf_life_days) || null,
      };
      const { data } = await api.post("/items", payload);
      toast.success(`Artikel "${data.name}" angelegt`);
      // Refresh + assign to the requesting line
      const list = (await api.get("/items")).data.filter((i) => i.type === "finished");
      setItems(list);
      if (newItemLineIdx != null) {
        const c = [...lines];
        c[newItemLineIdx].item_id = data.id;
        c[newItemLineIdx].batch_id = "";
        setLines(c);
      }
      setNewItemOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Artikel konnte nicht angelegt werden");
    } finally {
      setSavingItem(false);
    }
  };

  // Inline "neue Charge" pro Positions-Zeile (item_id vorbelegt)
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [newBatchLineIdx, setNewBatchLineIdx] = useState(null);
  const [newBatch, setNewBatch] = useState({
    item_id: "", charge_number: "", mhd: "", quantity: "",
    quality_status: "passed", location: "",
  });
  const [savingBatch, setSavingBatch] = useState(false);

  const openNewBatch = (idx) => {
    const l = lines[idx];
    if (!l?.item_id) { toast.error("Bitte zuerst einen Artikel wählen"); return; }
    const item = items.find((i) => i.id === l.item_id);
    const shelfLife = item?.default_shelf_life_days || 180;
    const d = new Date(); d.setDate(d.getDate() + shelfLife);
    setNewBatchLineIdx(idx);
    setNewBatch({
      item_id: l.item_id,
      charge_number: `WE-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${Math.floor(Math.random() * 900 + 100)}`,
      mhd: d.toISOString().slice(0, 10),
      quantity: l.quantity || "",
      quality_status: "passed",
      location: item?.warehouse_location || "",
    });
    setNewBatchOpen(true);
  };

  const saveNewBatch = async (e) => {
    e.preventDefault();
    if (!newBatch.charge_number.trim() || !newBatch.mhd || !newBatch.quantity) {
      return toast.error("Charge-Nr., MHD und Menge sind Pflicht");
    }
    setSavingBatch(true);
    try {
      const payload = {
        item_id: newBatch.item_id,
        charge_number: newBatch.charge_number.trim(),
        mhd: newBatch.mhd,
        quantity: Number(newBatch.quantity),
        quality_status: newBatch.quality_status,
        source: "production",
        location: newBatch.location || null,
      };
      const { data } = await api.post("/batches", payload);
      toast.success(`Charge "${data.charge_number}" angelegt`);
      const list = (await api.get("/batches")).data;
      setBatches(list);
      if (newBatchLineIdx != null) {
        const c = [...lines];
        c[newBatchLineIdx].batch_id = data.id;
        setLines(c);
      }
      setNewBatchOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Charge konnte nicht angelegt werden");
    } finally {
      setSavingBatch(false);
    }
  };

  const load = async () => {
    const r = await api.get("/delivery-notes", { params: { page, page_size: pageSize } });
    setRows(r.data);
    setTotal(Number(r.headers["x-total-count"]) || r.data.length);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/items").then((r) => setItems(r.data.filter((i) => i.type === "finished")));
    api.get("/batches").then((r) => setBatches(r.data));
    api.get("/settings/company").then((r) => setPalletCfg({
      default_exchange: r.data.pallet_exchange_default !== false,
      threshold: r.data.pallet_transport_threshold || 600,
    })).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        shipping_cost: Number(form.shipping_cost || 0),
        payment_terms_days: Number(form.payment_terms_days || 14),
        lines: lines.filter((l) => l.item_id && Number(l.quantity) > 0)
                    .map((l) => ({
                      item_id: l.item_id, batch_id: l.batch_id || null,
                      quantity: Number(l.quantity),
                      bundle_code: l.bundle_code || null,
                    })),
      };
      // Remove pallet fields — backend delivery-notes model doesn't know them
      delete payload.pallets_out; delete payload.pallets_returned;
      if (payload.lines.length === 0) return toast.error("Mindestens 1 Position nötig");
      const { data } = await api.post("/delivery-notes", payload);
      // Record pallets separately (idempotent)
      const pOut = Number(form.pallets_out || 0);
      const pRet = Number(form.pallets_returned || 0);
      if (pOut > 0 || pRet > 0) {
        try {
          const res = await api.post(`/pallets/delivery/${data.id}/record`, {
            delivered: pOut, returned: pRet,
          });
          if (res.data.needs_transport) {
            toast.warning(`Palettenschwelle erreicht (${res.data.open_balance} ≥ ${res.data.threshold}) — LKW-Transport organisieren`);
          }
        } catch (_err) { /* soft-fail */ }
      }
      toast.success("Lieferschein erstellt");
      setOpen(false);
      setForm({ customer_id: "", delivery_date: "", tour: "", notes: "", shipping_cost: 0, payment_terms_days: 14, pallets_out: 0, pallets_returned: 0 });
      setLines([{ item_id: "", batch_id: "", quantity: "", bundle_code: "" }]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const ship = async (id) => {
    try {
      await api.post(`/delivery-notes/${id}/ship`);
      toast.success("Versendet – Bestand gebucht");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const markDelivered = async (id) => {
    try {
      const { data } = await api.post(`/delivery-notes/${id}/mark-delivered`);
      toast.success(`Als geliefert markiert · Rechnung ${data.invoice_number}`);
      load();
      if (detail?.id === id) { openDetail(id); }
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const setShipping = async (id, patch) => {
    try {
      await api.patch(`/delivery-notes/${id}`, patch);
      load();
      if (detail?.id === id) {
        const { data } = await api.get(`/delivery-notes/${id}`);
        setDetail(data);
      }
    } catch (err) { toast.error("Fehler"); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/delivery-notes/${id}`);
    setDetail(data);
    try {
      const inv = (await api.get(`/delivery-notes/${id}/invoice`)).data;
      setInvoice(inv);
      setInvEdit({
        payment_terms_days: inv.payment_terms_days ?? 14,
        payment_status: inv.payment_status ?? "open",
        shipping_cost: inv.shipping_cost ?? 0,
        invoice_date: (inv.invoice_date || "").slice(0, 10),
      });
    } catch (_e) { setInvoice(null); }
  };
  const pdf = (id) => openPdf(`/delivery-notes/${id}/pdf`).catch(() => toast.error("PDF konnte nicht geöffnet werden"));
  const invoicePdf = (id) => openPdf(`/delivery-notes/${id}/invoice.pdf`).catch(() => toast.error("Rechnung konnte nicht geöffnet werden"));
  const quotePdf = (id) => openPdf(`/delivery-notes/${id}/quote.pdf`).catch(() => toast.error("Angebot konnte nicht geöffnet werden"));
  const stickerPdf = (id, variant = "pink") => openPdf(`/delivery-notes/${id}/sample-sticker.pdf?variant=${variant}`).catch(() => toast.error("Probe-Aufkleber konnte nicht geöffnet werden"));
  const shippingLabel = (id) => openPdf(`/delivery-notes/${id}/shipping-label.pdf`).catch(() => toast.error("Versandlabel konnte nicht geöffnet werden"));
  const resetDsv = async (id, number) => {
    if (!window.confirm(`DSV-Tracking von ${number} zurücksetzen (nach Stornierung)?`)) return;
    try {
      await api.post(`/delivery-notes/${id}/dsv-reset`);
      toast.success("DSV-Info gelöscht"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const bulkDsvBook = async () => {
    if (!selectedIds.length) return toast.error("Keine Lieferscheine markiert");
    if (!window.confirm(`${selectedIds.length} Lieferschein(e) an DSV übermitteln?`)) return;
    try {
      const { data } = await api.post("/dsv/bulk-book", { ids: selectedIds });
      toast.success(`${data.booked} gebucht · ${data.skipped} bereits vorhanden · ${data.errors?.length || 0} Fehler${data.sandbox ? " · SANDBOX" : ""}`);
      setSelected({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk-Buchung fehlgeschlagen"); }
  };

  const dhlLabel = (id) => openPdf(`/delivery-notes/${id}/dhl-label.pdf`).catch(() => toast.error("DHL-Label konnte nicht geöffnet werden"));

  const bookDsv = async (id, number) => {
    try {
      const { data } = await api.post(`/delivery-notes/${id}/dsv-shipment`);
      toast.success(`DSV gebucht · Tracking: ${data.tracking_number}${data.already_booked ? " (schon vorhanden)" : ""}${data.sandbox ? " · SANDBOX" : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler bei DSV-Buchung"); }
  };
  const dsvLabel = async (id) => {
    try {
      const res = await api.get(`/delivery-notes/${id}/dsv-label.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `dsv-label-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (_e) { toast.error("DSV-Label konnte nicht geladen werden"); }
  };
  const refreshDsvTracking = async (id, number) => {
    try {
      const { data } = await api.get(`/delivery-notes/${id}/dsv-tracking`);
      toast.info(`${number}: ${data.status_label || data.status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Tracking-Update fehlgeschlagen"); }
  };

  const confirm = useConfirm();
  const removeDelivery = async (r) => {
    const first = await confirm({
      title: `Lieferschein ${r.number} löschen?`,
      description: r.status === "shipped"
        ? "Der Lieferschein ist bereits versendet. Bei Bestätigung wird der Warenausgang rückgebucht."
        : "Lieferscheine mit ausgestellter Rechnung können nicht gelöscht werden.",
      confirmLabel: "Löschen",
      variant: "danger",
    });
    if (!first) return;
    try {
      const url = r.status === "shipped" ? `/delivery-notes/${r.id}?force=true` : `/delivery-notes/${r.id}`;
      const { data } = await api.delete(url);
      toast.success(`Lieferschein ${r.number} gelöscht${data.reversed_shipment ? " (Warenausgang rückgebucht)" : ""}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Fehler"));
    }
  };

  const bookDhl = async (id, number) => {
    const w = window.prompt(`Gewicht in Gramm für ${number}?`, "500");
    if (!w) return;
    try {
      const { data } = await api.post(`/delivery-notes/${id}/dhl-shipment`, { weight_grams: Number(w) });
      toast.success(`DHL gebucht · Tracking: ${data.tracking_number}${data.already_booked ? " (schon vorhanden)" : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler bei DHL-Buchung"); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const filteredRows = rows.filter((r) => {
    if (custFilter !== "all" && (r.customer_type || "B2B") !== custFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.number || "").toLowerCase().includes(s)
        || (r.customer_name || "").toLowerCase().includes(s)
        || (r.order_number || r.woo_order_number || "").toString().toLowerCase().includes(s);
  });
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };
  const toggleAll = () => {
    const allOn = rows.length > 0 && rows.every((r) => selected[r.id]);
    const next = { ...selected };
    rows.forEach((r) => { next[r.id] = !allOn; });
    setSelected(next);
  };
  const bulkInvoicesZip = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} Rechnung(en) als ZIP erzeugen und herunterladen?`)) return;
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await postDownload("/delivery-notes/bulk-invoices.zip",
                         { delivery_note_ids: selectedIds },
                         `rechnungen-${stamp}.zip`);
      toast.success(`${selectedIds.length} Rechnung(en) heruntergeladen`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bulk-Rechnungen");
    }
  };

  const bulkInvoicesEmail = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} Rechnung(en) erzeugen und per Mail an die Kunden verschicken?`)) return;
    try {
      const { data } = await api.post("/delivery-notes/bulk-invoices-email", {
        delivery_note_ids: selectedIds,
      });
      const errCount = (data.errors || []).length;
      toast.success(`${data.sent} Mail(s) verschickt · ${data.skipped_no_email} ohne E-Mail${errCount ? ` · ${errCount} Fehler` : ""}`);
      if (errCount) console.warn("Bulk-Email errors:", data.errors);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bulk-Versand");
    }
  };

  const bulkShippingLabels = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} Versandlabel(s) drucken? (Öffnet jedes Label in einem neuen Tab)`)) return;
    // Open PDF URLs sequentially with a short delay so the browser popup-blocker
    // doesn't kill the batch. The API endpoint uses cookies, so no auth header needed.
    const base = api.defaults.baseURL;
    for (const id of selectedIds) {
      window.open(`${base}/delivery-notes/${id}/shipping-label.pdf`, "_blank", "noopener");
      // small pause between tabs
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 250));
    }
    toast.success(`${selectedIds.length} Label(s) geöffnet — bitte Popup-Blocker prüfen`);
    setSelected({});
  };

  const saveInvoice = async () => {
    if (!detail) return;
    try {
      await api.patch(`/delivery-notes/${detail.id}/invoice`, {
        payment_terms_days: Number(invEdit.payment_terms_days),
        payment_status: invEdit.payment_status,
        shipping_cost: Number(invEdit.shipping_cost),
        invoice_date: invEdit.invoice_date || null,
      });
      toast.success("Rechnung aktualisiert");
      openDetail(detail.id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const batchesForItem = (item_id) => batches.filter((b) => b.item_id === item_id);

  return (
    <div data-testid="page-deliveries">
      <PageHeader
        eyebrow="Versand"
        title="Lieferscheine"
        description="Kommissionierung, Touren, Versandpartner (DHL/DPD) und PDF-Ausdruck."
        actions={
          <div className="flex flex-wrap items-center gap-2 justify-end">
          <Button variant="outline" data-testid="btn-import-deliveries"
                  onClick={() => setImportOpen(true)}
                  className="rounded-full">
            <FileUp size={14} className="mr-1" /> Importieren
          </Button>
          <Button variant="outline" data-testid="btn-dsv-pickup"
                  onClick={() => setPickupOpen(true)}
                  className="rounded-full border-[#003580] text-[#003580] hover:bg-[#003580]/5">
            <Truck size={14} className="mr-1" /> DSV Abholung
          </Button>
          {selectedIds.length > 0 && (
            <Button variant="outline" data-testid="btn-bulk-dsv-book"
                    onClick={bulkDsvBook}
                    className="rounded-full bg-[#003580] text-white hover:opacity-90 font-bold">
              <Send size={14} className="mr-1" /> {selectedIds.length}× DSV buchen
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-delivery" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"><Plus size={16} className="mr-1" /> Neuer Lieferschein</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Neuer Lieferschein</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Kunde *</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <CustomerAutocomplete
                          value={form.customer_id}
                          onChange={(id) => setForm({ ...form, customer_id: id })}
                          customers={customers}
                          serverSearch={true}
                          testId="dn-customer-autocomplete"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={openNewCust}
                        data-testid="btn-inline-new-customer"
                        title="Neuen Kunden anlegen"
                        className="shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)]"
                      >
                        <UserPlus size={16} />
                      </button>
                    </div>
                  </div>
                  <div><Label>Datum</Label><Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></div>
                  <div><Label>Tour</Label><Input value={form.tour} onChange={(e) => setForm({ ...form, tour: e.target.value })} placeholder="z.B. Nord-Berlin" /></div>
                  <div><Label>Zahlungsziel (Tage)</Label><Input type="number" value={form.payment_terms_days} data-testid="input-dn-payment-terms" onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Versandkosten (netto €, 19 % MwSt)</Label>
                    <Input type="number" step="0.01" value={form.shipping_cost}
                           data-testid="input-dn-shipping"
                           onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} />
                  </div>
                </div>
                {/* Palettentausch */}
                <div className="border rounded-2xl p-3 bg-[var(--pop-cream)]/30" data-testid="pallet-exchange-section">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="overline">Palettentausch</Label>
                    {palletCfg.default_exchange && (
                      <span className="text-[10px] font-bold text-[var(--pop-pink)] uppercase">Standard: Tausch bevorzugt</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Paletten hin (POPCRN → Kunde)</Label>
                      <Input type="number" min="0" data-testid="input-dn-pallets-out"
                             value={form.pallets_out}
                             onChange={(e) => {
                               const v = e.target.value;
                               setForm((f) => ({
                                 ...f,
                                 pallets_out: v,
                                 // Auto-fill returned wenn default_exchange und Kunde nichts anderes eingetragen
                                 pallets_returned: palletCfg.default_exchange && (!f.pallets_returned || String(f.pallets_returned) === String(f.pallets_out))
                                   ? v : f.pallets_returned,
                               }));
                             }} />
                    </div>
                    <div>
                      <Label>Paletten zurück (Kunde → POPCRN)</Label>
                      <Input type="number" min="0" data-testid="input-dn-pallets-returned"
                             value={form.pallets_returned}
                             onChange={(e) => setForm({ ...form, pallets_returned: e.target.value })} />
                    </div>
                  </div>
                  {Number(form.pallets_out || 0) !== Number(form.pallets_returned || 0) &&
                   Number(form.pallets_out || 0) + Number(form.pallets_returned || 0) > 0 && (
                    <div className="text-xs text-[var(--text-muted)] mt-2" data-testid="pallet-diff-hint">
                      Differenz: <b className={Number(form.pallets_out || 0) - Number(form.pallets_returned || 0) > 0 ? "text-red-700" : "text-blue-700"}>
                        {(Number(form.pallets_out || 0) - Number(form.pallets_returned || 0)) > 0 ? "+" : ""}
                        {Number(form.pallets_out || 0) - Number(form.pallets_returned || 0)}
                      </b>
                      {" "}wird als offene Palettenschuld beim Kunden vermerkt.
                    </div>
                  )}
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2">
                    <Label className="overline">Positionen</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setLines([...lines, { item_id: "", batch_id: "", quantity: "", bundle_code: "" }])} className="rounded-full"><Plus size={14} /> Zeile</Button>
                  </div>
                  <div className="space-y-2">
                    {lines.map((l, idx) => (
                      <div key={idx} className="rounded-xl border border-[var(--border)] p-3 space-y-2 sm:border-0 sm:rounded-none sm:p-0 sm:space-y-0 sm:grid sm:grid-cols-[1fr_36px_1fr_36px_110px_90px_40px] sm:gap-2 sm:items-center">
                        <div className="flex gap-2 sm:contents">
                          <div className="flex-1 min-w-0">
                            <ItemCombobox
                              items={items}
                              value={l.item_id}
                              onChange={async (v) => {
                                const c = [...lines]; c[idx].item_id = v; c[idx].batch_id = "";
                                if (v && form.customer_id) {
                                  try {
                                    const { data } = await api.get(`/customers/${form.customer_id}/prices/${v}`);
                                    const bp = (data && data.bundle_prices) || {};
                                    const bcode = c[idx].bundle_code;
                                    let p = null;
                                    if (bcode && bp[bcode] != null) p = bp[bcode];
                                    else if (!bcode && data && data.price != null) p = data.price;
                                    if (p != null) { c[idx].price = p; c[idx].is_customer_price = true; }
                                    else { c[idx].is_customer_price = false; }
                                  } catch (_e) { c[idx].is_customer_price = false; }
                                }
                                setLines(c);
                              }}
                              placeholder="Fertigware…"
                              testId={`select-dn-item-${idx}`}
                              onCreate={(term) => openNewItem(idx, term)}
                            />
                          </div>
                          <button type="button" onClick={() => openNewItem(idx)}
                                  data-testid={`btn-inline-new-item-${idx}`}
                                  title="Neuen Artikel anlegen"
                                  className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)]">
                            <PackagePlus size={14} />
                          </button>
                        </div>
                        <div className="flex gap-2 sm:contents">
                          <div className="flex-1 min-w-0">
                            <BatchCombobox
                              batches={batchesForItem(l.item_id)}
                              value={l.batch_id || ""}
                              onChange={(v) => { const c = [...lines]; c[idx].batch_id = v; setLines(c); }}
                              testId={`select-dn-batch-${idx}`}
                              disabled={!l.item_id}
                              onCreate={l.item_id ? (() => openNewBatch(idx)) : undefined}
                            />
                          </div>
                          <button type="button" onClick={() => openNewBatch(idx)}
                                  data-testid={`btn-inline-new-batch-${idx}`}
                                  title={l.item_id ? "Neue Charge anlegen" : "Erst Artikel wählen"}
                                  disabled={!l.item_id}
                                  className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] disabled:opacity-30 disabled:cursor-not-allowed">
                            <Layers size={14} />
                          </button>
                        </div>
                        <div className="flex gap-2 items-center sm:contents">
                          <Input type="number" step="any" placeholder="Menge" value={l.quantity} className="flex-1 sm:flex-none" onChange={(e) => { const c = [...lines]; c[idx].quantity = e.target.value; setLines(c); }} />
                          <Select value={l.bundle_code || "none"} onValueChange={async (v) => {
                            const c = [...lines]; const bcode = v === "none" ? "" : v; c[idx].bundle_code = bcode;
                            if (c[idx].item_id && form.customer_id) {
                              try {
                                const { data } = await api.get(`/customers/${form.customer_id}/prices/${c[idx].item_id}`);
                                const bp = (data && data.bundle_prices) || {};
                                let p = null;
                                if (bcode && bp[bcode] != null) p = bp[bcode];
                                else if (!bcode && data && data.price != null) p = data.price;
                                if (p != null) { c[idx].price = p; c[idx].is_customer_price = true; }
                              } catch (_e) { /* kein Kundenpreis */ }
                            }
                            setLines(c);
                          }}>
                            <SelectTrigger data-testid={`select-dn-bundle-${idx}`} className="text-xs w-28 sm:w-full"><SelectValue placeholder="Einzel" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Einzel (stk)</SelectItem>
                              <SelectItem value="13er">13er</SelectItem>
                              <SelectItem value="40er">40er</SelectItem>
                              <SelectItem value="120er">120er Display</SelectItem>
                              <SelectItem value="palette">Palette</SelectItem>
                            </SelectContent>
                          </Select>
                          <button type="button" onClick={() => setLines(lines.filter((_, i) => i !== idx))} className="text-red-500 shrink-0"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-dn" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 items-start">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          {selectedIds.length > 0 && (
            <div className="p-3 border-b border-[var(--border)] bg-[var(--pop-cream)]/60 flex items-center justify-between">
              <div className="text-sm">
                <b>{selectedIds.length}</b> Lieferschein(e) ausgewählt
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setSelected({})} className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]">
                  Auswahl aufheben
                </button>
                <button onClick={bulkShippingLabels} data-testid="btn-bulk-shipping-labels"
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-caramel)] text-white px-4 py-1.5 text-sm font-bold hover:opacity-90">
                  <Package size={14} /> Label drucken ({selectedIds.length})
                </button>
                <button onClick={bulkInvoicesEmail} data-testid="btn-bulk-invoices-email"
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-pink)] text-white px-4 py-1.5 text-sm font-bold hover:opacity-90">
                  <Send size={14} /> Als Rechnung per Mail
                </button>
                <button onClick={bulkInvoicesZip} data-testid="btn-bulk-invoices"
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--success)] text-white px-4 py-1.5 text-sm font-bold hover:opacity-90">
                  <Receipt size={14} /> Als Rechnung (ZIP)
                </button>
              </div>
            </div>
          )}
          {/* Suche + Filter */}
          <div className="p-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
            <button onClick={toggleAll} data-testid="deliveries-select-all"
                    className="shrink-0 hover:opacity-70" title="Alle auf dieser Seite auswählen">
              {filteredRows.length > 0 && filteredRows.every((r) => selected[r.id])
                ? <CheckSquare size={18} className="text-[var(--pop-pink)]" />
                : <Square size={18} className="text-[var(--text-muted)]" />}
            </button>
            <div className="relative flex-1 min-w-[180px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <Input placeholder="Suche Nr., Kunde, Bestellnr…" value={q}
                     data-testid="input-deliveries-search"
                     onChange={(e) => setQ(e.target.value)}
                     className="pl-8 rounded-xl h-9 text-sm" />
            </div>
            <FilterPills
              options={[
                ["all", "Alle", "#e5e5e5"],
                ["B2B", "B2B", "#93c5fd"],
                ["B2C", "B2C", "#fbcfe8"],
              ]}
              value={custFilter}
              onChange={setCustFilter}
              testidPrefix="filter-kunde-"
            />
            <span className="text-xs text-[var(--text-muted)] ml-auto">
              {filteredRows.length} / {rows.length}
            </span>
          </div>

          {/* Karten-/Listenansicht */}
          <div className="p-3 space-y-2" data-testid="deliveries-list">
            {filteredRows.map((r) => {
              const canShip = r.status !== "shipped" && r.status !== "delivered";
              return (
              <div key={r.id} data-testid={`dn-card-${r.number}`}
                   onClick={() => openDetail(r.id)}
                   className={`group rounded-xl border p-4 cursor-pointer transition-colors ${
                     selected[r.id] ? "border-[var(--pop-pink)] bg-[var(--surface-alt)]" : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--pop-pink)]/50"
                   }`}>
                <div className="flex items-start gap-3">
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => toggleSelect(r.id, e)} data-testid={`select-dn-${r.number}`} className="hover:opacity-70">
                      {selected[r.id] ? <CheckSquare size={18} className="text-[var(--pop-pink)]" /> : <Square size={18} className="text-[var(--text-muted)]" />}
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-bold text-base truncate min-w-0 flex items-center gap-2">
                        {r.customer_name || "—"}
                        {r.is_sample && <span className="pop-pill shrink-0" style={{ background: "#fce7f3", color: "#9d174d", fontSize: "10px" }}>🎁 PROBE</span>}
                      </div>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs text-[var(--text-muted)] font-mono">
                      <span>{r.number}</span>
                      <span>· {r.delivery_date || "—"}</span>
                      {r.tour && <span>· Tour {r.tour}</span>}
                      {r.dsv_tracking_number && (
                        <span className="pop-pill text-[10px]" style={{ background: "#003580", color: "#fff" }} title={`DSV ${r.dsv_tracking_number}`}>DSV {r.dsv_status || "gebucht"}</span>
                      )}
                      {r.dhl_tracking_number && (
                        <span className="pop-pill text-[10px]" style={{ background: "#FFCC00", color: "#171717" }} title={`DHL ${r.dhl_tracking_number}`}>DHL</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {canShip && (
                        <button data-testid={`btn-ship-${r.number}`} onClick={() => ship(r.id)}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--info)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <Truck size={13} /> Versenden
                        </button>
                      )}
                      {r.status === "shipped" && (
                        <button data-testid={`btn-delivered-${r.number}`} onClick={() => markDelivered(r.id)}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--success)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <PackageCheck size={13} /> Geliefert
                        </button>
                      )}
                      <button data-testid={`btn-pdf-${r.number}`} onClick={() => pdf(r.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)]">
                        <FileText size={13} /> LS
                      </button>
                      <button data-testid={`btn-invoice-${r.number}`} onClick={() => invoicePdf(r.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)]">
                        <Receipt size={13} /> Rechnung
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`dn-menu-${r.number}`} title="Weitere Aktionen"
                                  className="ml-auto h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--pop-pink)]">
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {r.is_sample && (
                            <DropdownMenuItem data-testid={`btn-sticker-${r.number}`} onClick={() => stickerPdf(r.id)}>
                              <Sticker size={14} className="mr-2" /> Probe-Aufkleber
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`btn-quote-${r.number}`} onClick={() => quotePdf(r.id)}>
                            <FileSignature size={14} className="mr-2" /> Angebot
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`btn-shipping-label-${r.number}`} onClick={() => shippingLabel(r.id)}>
                            <Package size={14} className="mr-2" /> Versandlabel
                          </DropdownMenuItem>
                          {canShip && (
                            <DropdownMenuItem data-testid={`btn-mark-shipping-${r.number}`} onClick={() => setShipping(r.id, { shipping_ready: !r.shipping_ready })}>
                              <Truck size={14} className="mr-2" /> {r.shipping_ready ? "Vormerkung aufheben" : "Zum Versand markieren"}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {r.dsv_tracking_number ? (
                            <>
                              <DropdownMenuItem data-testid={`btn-dsv-label-${r.number}`} onClick={() => dsvLabel(r.id)}>
                                <Truck size={14} className="mr-2" /> DSV-Label
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`btn-dsv-track-${r.number}`} onClick={() => refreshDsvTracking(r.id, r.number)}>
                                <Send size={14} className="mr-2" /> DSV-Status aktualisieren
                              </DropdownMenuItem>
                              {r.dsv_pod_pdf_b64 && (
                                <DropdownMenuItem data-testid={`btn-dsv-pod-${r.number}`} onClick={() => window.open(`${API_BASE}/delivery-notes/${r.id}/dsv-pod.pdf`, "_blank", "noopener")}>
                                  <FileText size={14} className="mr-2" /> Zustellnachweis (PoD)
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem data-testid={`btn-dsv-reset-${r.number}`} onClick={() => resetDsv(r.id, r.number)} className="text-red-600">
                                <Trash2 size={14} className="mr-2" /> DSV zurücksetzen
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem data-testid={`btn-book-dsv-${r.number}`} onClick={() => bookDsv(r.id, r.number)}>
                              <Send size={14} className="mr-2" /> DSV buchen
                            </DropdownMenuItem>
                          )}
                          {r.dhl_tracking_number ? (
                            <DropdownMenuItem data-testid={`btn-dhl-label-${r.number}`} onClick={() => dhlLabel(r.id)}>
                              <Truck size={14} className="mr-2" /> DHL-Label
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem data-testid={`btn-book-dhl-${r.number}`} onClick={() => bookDhl(r.id, r.number)}>
                              <Send size={14} className="mr-2" /> DHL buchen
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`btn-delete-dn-${r.number}`} onClick={() => removeDelivery(r)} className="text-red-600">
                            <Trash2 size={14} className="mr-2" /> Lieferschein löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            {filteredRows.length === 0 && (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]" data-testid="deliveries-empty">
                {rows.length === 0 ? "Keine Lieferscheine." : "Keine Lieferscheine für diesen Filter."}
              </div>
            )}
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} onPageSizeChange={(n)=>{setPage(1);setPageSize(n);}} storageKey="deliveries" testIdPrefix="deliveries-pager" />
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setInvoice(null); } }}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="delivery-detail-modal"
        >
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-3">
                  Lieferschein {detail.number}
                  {detail.status && <StatusBadge status={detail.status} />}
                </DialogTitle>
              </DialogHeader>

              <div className="text-sm text-[var(--text-muted)] -mt-2 mb-4">
                {detail.customer?.name} · {detail.customer?.city}
                {detail.delivery_date ? ` · ${detail.delivery_date}` : ""}
                {detail.tour ? ` · Tour ${detail.tour}` : ""}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <Label>Versand</Label>
                  <Select value={detail.carrier || "none"} onValueChange={(v) => setShipping(detail.id, { carrier: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="select-detail-carrier"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="DHL">DHL</SelectItem>
                      <SelectItem value="DPD">DPD</SelectItem>
                      <SelectItem value="UPS">UPS</SelectItem>
                      <SelectItem value="Selbstabholung">Selbstabholung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tracking</Label>
                  <Input value={detail.tracking_number || ""} onBlur={(e) => setShipping(detail.id, { tracking_number: e.target.value })} data-testid="input-detail-tracking" />
                </div>
              </div>

              <div className="overline mb-2">Positionen</div>
              <ul className="divide-y divide-[var(--border)] mb-2">
                {detail.lines?.map((l, i) => (
                  <li key={i} className="py-2 flex justify-between">
                    <div>
                      <div className="text-sm font-medium">{l.item_name}</div>
                      <div className="text-xs font-mono text-[var(--text-muted)]">
                        {l.item_sku}{l.charge_number ? ` · Charge ${l.charge_number}` : ""}{l.mhd ? ` · MHD ${l.mhd}` : ""}
                      </div>
                    </div>
                    <div className="font-mono text-sm">{l.quantity} {l.item_unit}</div>
                  </li>
                ))}
              </ul>

              <PackagingSection detail={detail}
                                onSaved={async () => {
                                  const { data } = await api.get(`/delivery-notes/${detail.id}`);
                                  setDetail(data); load();
                                }} />

              <div className="mt-4 rounded-2xl border border-[var(--border)] p-3">
                <PhotoAttachments refType="outbound" refId={detail.id}
                                  label="Foto-Nachweis Warenausgang / Palette" />
              </div>

              <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-[var(--border)] p-3 bg-[var(--pop-cream)]/30">
                <div className="text-sm">
                  <div className="font-semibold">Palettenschein</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    Übergabebeleg mit hin/zurück, Unterschriftsfeldern und QR
                    {(detail.pallets_out || detail.pallets_returned)
                      ? ` · ${detail.pallets_out || 0} hin / ${detail.pallets_returned || 0} zurück`
                      : " · (keine Paletten gebucht)"}
                  </div>
                </div>
                <button
                  data-testid={`btn-pallet-note-${detail.number}`}
                  onClick={() => openPdf(`/delivery-notes/${detail.id}/pallet-note.pdf`).catch(() => toast.error("Palettenschein-Fehler"))}
                  className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-pink)] text-white text-sm font-bold px-4 py-2 hover:opacity-90">
                  <FileText size={14} /> Palettenschein
                </button>
              </div>

              {invoice && (
                <div className="mt-6 border-t border-[var(--border)] pt-4" data-testid="invoice-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="overline">Rechnung</div>
                      <div className="font-display font-bold text-xl">{invoice.invoice_number || "— noch keine Nr. —"}</div>
                    </div>
                    <div className="text-right">
                      <div className="overline">Brutto</div>
                      <div className="font-mono font-semibold text-lg">{(invoice.gross_total || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    {Object.entries(invoice.net_by_rate || {}).map(([rate, net]) => (
                      <div key={rate} className="flex justify-between px-2 py-1 rounded bg-neutral-50">
                        <span className="text-[var(--text-muted)]">Netto {Math.round(Number(rate) * 100)} %</span>
                        <span className="font-mono">{Number(net).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                      </div>
                    ))}
                    {Object.entries(invoice.vat_by_rate || {}).map(([rate, vat]) => (
                      <div key={"v" + rate} className="flex justify-between px-2 py-1 rounded bg-neutral-50">
                        <span className="text-[var(--text-muted)]">MwSt {Math.round(Number(rate) * 100)} %</span>
                        <span className="font-mono">{Number(vat).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label>Zahlungsziel (Tage)</Label>
                      <Input type="number" data-testid="input-inv-terms"
                             value={invEdit.payment_terms_days}
                             onChange={(e) => setInvEdit({ ...invEdit, payment_terms_days: e.target.value })} />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={invEdit.payment_status} onValueChange={(v) => setInvEdit({ ...invEdit, payment_status: v })}>
                        <SelectTrigger data-testid="select-inv-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Offen</SelectItem>
                          <SelectItem value="partial">Teilbezahlt</SelectItem>
                          <SelectItem value="paid">Bezahlt</SelectItem>
                          <SelectItem value="overdue">Überfällig</SelectItem>
                          <SelectItem value="cancelled">Storniert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rechnungsdatum</Label>
                      <Input type="date" data-testid="input-inv-date"
                             value={invEdit.invoice_date || ""}
                             onChange={(e) => setInvEdit({ ...invEdit, invoice_date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Versandkosten (€)</Label>
                      <Input type="number" step="0.01" data-testid="input-inv-shipping"
                             value={invEdit.shipping_cost}
                             onChange={(e) => setInvEdit({ ...invEdit, shipping_cost: e.target.value })} />
                    </div>
                  </div>

                  {invoice.due_date && (
                    <div className="text-xs text-[var(--text-muted)] mb-3">
                      Fällig am <span className="font-mono">{invoice.due_date}</span>
                    </div>
                  )}
                  {(detail?.invoice_emailed_at || detail?.dsv_pod_pdf_b64) && (
                    <div className="mt-3 rounded-lg border border-[var(--border)] bg-neutral-50 p-3 text-xs space-y-1" data-testid="email-history">
                      <div className="overline text-[10px]">E-Mail-Historie</div>
                      {detail?.invoice_emailed_at ? (
                        <div data-testid="invoice-emailed-at">
                          📧 Rechnung {detail?.dsv_pod_pdf_b64 ? "+ PoD " : ""}zuletzt versendet am{" "}
                          <b>{new Date(detail.invoice_emailed_at).toLocaleString("de-DE")}</b>
                          {detail.invoice_emailed_to && <> an <span className="font-mono">{detail.invoice_emailed_to}</span></>}
                        </div>
                      ) : (
                        <div className="text-[var(--text-muted)]">Rechnung wurde noch nicht per E-Mail verschickt.</div>
                      )}
                      {detail?.dsv_pod_pdf_b64 && (
                        <div>📎 Zustellnachweis (PoD) wird automatisch an die Rechnungsmail angehängt.</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="mt-4 gap-2 flex-wrap">
                {invoice && (
                  <Button data-testid="btn-save-invoice" onClick={saveInvoice}
                          className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                    Rechnung speichern
                  </Button>
                )}
                <Button data-testid="btn-open-invoice-pdf" onClick={() => invoicePdf(detail.id)}
                        variant="outline" className="rounded-full">
                  <Receipt size={14} className="mr-1" /> Rechnung
                </Button>
                <Button onClick={() => pdf(detail.id)} variant="outline" className="rounded-full">
                  <FileText size={14} className="mr-1" /> Lieferschein
                </Button>
                <Button onClick={() => { setDetail(null); setInvoice(null); }} variant="outline" className="rounded-full">
                  Schließen
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Inline: Neuen Kunden anlegen aus dem Lieferschein-Dialog heraus */}
      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-inline-new-customer">
          <DialogHeader><DialogTitle className="font-display text-2xl">Neuer Kunde</DialogTitle></DialogHeader>
          <form onSubmit={saveNewCust} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input required data-testid="inline-cust-name" value={newCust.name}
                     onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} />
            </div>
            <div>
              <Label>Typ</Label>
              <Select value={newCust.type} onValueChange={(v) => setNewCust({ ...newCust, type: v })}>
                <SelectTrigger data-testid="inline-cust-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2B">B2B (Firma)</SelectItem>
                  <SelectItem value="B2C">B2C (Privatkunde)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zahlungsziel (Tage)</Label>
              <Input type="number" value={newCust.payment_terms_days}
                     onChange={(e) => setNewCust({ ...newCust, payment_terms_days: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" data-testid="inline-cust-email" value={newCust.email}
                     onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={newCust.phone}
                     onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Straße</Label>
              <Input value={newCust.street} data-testid="inline-cust-street"
                     onChange={(e) => setNewCust({ ...newCust, street: e.target.value })} />
            </div>
            <div>
              <Label>PLZ</Label>
              <Input value={newCust.zip} data-testid="inline-cust-zip"
                     onChange={(e) => setNewCust({ ...newCust, zip: e.target.value })} />
            </div>
            <div>
              <Label>Ort</Label>
              <Input value={newCust.city} data-testid="inline-cust-city"
                     onChange={(e) => setNewCust({ ...newCust, city: e.target.value })} />
            </div>
            <div>
              <Label>Land</Label>
              <Input value={newCust.country}
                     onChange={(e) => setNewCust({ ...newCust, country: e.target.value })} />
            </div>
            <DialogFooter className="col-span-2 gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setNewCustOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={savingCust} data-testid="btn-save-inline-customer"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                {savingCust ? "Speichere…" : "Speichern & übernehmen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inline: Neuen Artikel anlegen aus einer Positions-Zeile heraus */}
      <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-inline-new-item">
          <DialogHeader><DialogTitle className="font-display text-2xl">Neuer Artikel (Fertigware)</DialogTitle></DialogHeader>
          <form onSubmit={saveNewItem} className="grid grid-cols-2 gap-3">
            <div>
              <Label>SKU *</Label>
              <Input required data-testid="inline-item-sku" value={newItem.sku}
                     placeholder="z.B. POP-CHILI-100G"
                     onChange={(e) => setNewItem({ ...newItem, sku: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>Barcode / EAN</Label>
              <Input data-testid="inline-item-barcode" value={newItem.barcode}
                     onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input required data-testid="inline-item-name" value={newItem.name}
                     onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
            </div>
            <div>
              <Label>Einheit</Label>
              <Select value={newItem.unit} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                <SelectTrigger data-testid="inline-item-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stk">Stück</SelectItem>
                  <SelectItem value="kg">Kilogramm</SelectItem>
                  <SelectItem value="g">Gramm</SelectItem>
                  <SelectItem value="l">Liter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>MHD-Laufzeit (Tage)</Label>
              <Input type="number" data-testid="inline-item-shelflife" value={newItem.default_shelf_life_days}
                     onChange={(e) => setNewItem({ ...newItem, default_shelf_life_days: e.target.value })} />
            </div>
            <div>
              <Label>Preis (€ netto)</Label>
              <Input type="number" step="0.01" data-testid="inline-item-price" value={newItem.price}
                     onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
            </div>
            <div>
              <Label>MwSt-Satz</Label>
              <Select value={String(newItem.vat_rate)} onValueChange={(v) => setNewItem({ ...newItem, vat_rate: Number(v) })}>
                <SelectTrigger data-testid="inline-item-vat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.07">7 % (Lebensmittel)</SelectItem>
                  <SelectItem value="0.19">19 % (Standard)</SelectItem>
                  <SelectItem value="0">0 %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="col-span-2 gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setNewItemOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={savingItem} data-testid="btn-save-inline-item"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                {savingItem ? "Speichere…" : "Speichern & übernehmen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inline: Neue Charge anlegen aus einer Positions-Zeile heraus */}
      <Dialog open={newBatchOpen} onOpenChange={setNewBatchOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-inline-new-batch">
          <DialogHeader><DialogTitle className="font-display text-2xl">Neue Charge</DialogTitle></DialogHeader>
          <form onSubmit={saveNewBatch} className="grid grid-cols-2 gap-3">
            <div className="col-span-2 text-sm">
              Artikel: <b>{items.find((i) => i.id === newBatch.item_id)?.name || "—"}</b>
              <span className="ml-2 text-[var(--text-muted)] font-mono">{items.find((i) => i.id === newBatch.item_id)?.sku}</span>
            </div>
            <div>
              <Label>Charge-Nr *</Label>
              <Input required data-testid="inline-batch-charge" value={newBatch.charge_number}
                     onChange={(e) => setNewBatch({ ...newBatch, charge_number: e.target.value })} className="font-mono" />
            </div>
            <div>
              <Label>MHD *</Label>
              <Input required type="date" data-testid="inline-batch-mhd" value={newBatch.mhd}
                     onChange={(e) => setNewBatch({ ...newBatch, mhd: e.target.value })} />
            </div>
            <div>
              <Label>Produzierte Menge *</Label>
              <Input required type="number" step="any" data-testid="inline-batch-quantity"
                     value={newBatch.quantity}
                     onChange={(e) => setNewBatch({ ...newBatch, quantity: e.target.value })} />
            </div>
            <div>
              <Label>Regal / Lagerplatz</Label>
              <Input data-testid="inline-batch-location" value={newBatch.location}
                     placeholder="z.B. A-01-03"
                     onChange={(e) => setNewBatch({ ...newBatch, location: e.target.value })} className="font-mono" />
            </div>
            <div className="col-span-2">
              <Label>Qualitätsstatus</Label>
              <Select value={newBatch.quality_status} onValueChange={(v) => setNewBatch({ ...newBatch, quality_status: v })}>
                <SelectTrigger data-testid="inline-batch-quality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="passed">Freigegeben</SelectItem>
                  <SelectItem value="pending">Zur Prüfung</SelectItem>
                  <SelectItem value="blocked">Gesperrt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="col-span-2 gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setNewBatchOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={savingBatch} data-testid="btn-save-inline-batch"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                {savingBatch ? "Speichere…" : "Speichern & übernehmen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-dsv-pickup">
          <DialogHeader><DialogTitle className="font-display text-2xl">DSV-Abholung beauftragen</DialogTitle></DialogHeader>
          <form onSubmit={submitPickup} className="space-y-3">
            <div><Label>Abholdatum</Label>
              <Input type="date" required data-testid="input-pickup-date"
                      value={pickupForm.pickup_date}
                      onChange={(e) => setPickupForm({ ...pickupForm, pickup_date: e.target.value })} /></div>
            <div><Label>Zeitfenster</Label>
              <select className="w-full h-10 rounded-xl border border-[var(--border)] px-3 text-sm"
                       data-testid="select-pickup-window"
                       value={pickupForm.time_window}
                       onChange={(e) => setPickupForm({ ...pickupForm, time_window: e.target.value })}>
                <option value="09:00-12:00">09:00 – 12:00</option>
                <option value="12:00-15:00">12:00 – 15:00</option>
                <option value="15:00-18:00">15:00 – 18:00</option>
              </select></div>
            <div><Label>Anzahl Pakete</Label>
              <Input type="number" min="1" required data-testid="input-pickup-count"
                      value={pickupForm.package_count}
                      onChange={(e) => setPickupForm({ ...pickupForm, package_count: Number(e.target.value) })} /></div>
            <div><Label>Notizen (optional)</Label>
              <Input value={pickupForm.notes} placeholder="z. B. Rampe 3, klingeln bei Halle B"
                      data-testid="input-pickup-notes"
                      onChange={(e) => setPickupForm({ ...pickupForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="btn-submit-pickup"
                       className="rounded-full bg-[#003580] text-white hover:opacity-90 font-bold">
                Abholung buchen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      <ImportDialog open={importOpen} onOpenChange={setImportOpen}
        entityLabel="Lieferscheine"
        previewEndpoint="/deliveries/import/preview"
        commitEndpoint="/deliveries/import/commit"
        templateUrl={`${API_BASE}/deliveries/import/template.xlsx`}
        previewKey="rows" onDone={() => load()}
        renderPreviewHeader={() => (<>
          <th className="text-left p-2">LS-Nr.</th>
          <th className="text-left p-2">Datum</th>
          <th className="text-left p-2">Kunde</th>
          <th className="text-right p-2">Positionen</th>
          <th className="text-left p-2">Aktion</th>
        </>)}
        renderPreviewRow={(row, i) => {
          const b = row.action === "create" ? "bg-green-100 text-green-700"
                  : row.action === "update" ? "bg-blue-100 text-blue-700"
                  : "bg-red-100 text-red-700";
          return (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className="p-2 font-mono font-bold">{row.delivery_number}</td>
              <td className="p-2 text-xs">{row.delivery_date || "—"}</td>
              <td className="p-2 text-xs">{row.customer || "—"}</td>
              <td className="p-2 text-right font-mono">{row.line_count}</td>
              <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${b}`}>{row.action}</span></td>
            </tr>
          );
        }} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Verpackungsmaterial pro Sendung (Runde 124.8) — Palette, Karton, Folie …
// werden hier gepflegt, beim „Versenden"-Klick automatisch als `out` gebucht
// und auf dem Lieferschein als eigene Sektion gedruckt.
// -----------------------------------------------------------------------------
function PackagingSection({ detail, onSaved }) {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmedOverdraw, setConfirmedOverdraw] = useState(false);

  useEffect(() => {
    api.get("/items").then((r) => setItems((r.data || []).filter((i) => (i.type || "").toLowerCase() === "packaging")))
       .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    setRows((detail?.packaging_items || []).map((p) => ({
      item_id: p.item_id || "",
      quantity: p.quantity ?? 1,
      is_pallet: !!p.is_pallet,
      note: p.note || "",
    })));
  }, [detail?.id, detail?.packaging_items?.length]);

  const addRow = () => setRows([...rows, { item_id: "", quantity: 1, is_pallet: false, note: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const clean = rows
        .filter((r) => r.item_id && Number(r.quantity) > 0)
        .map((r) => ({
          item_id: r.item_id,
          quantity: Number(r.quantity),
          is_pallet: !!r.is_pallet,
          note: r.note || null,
        }));
      // Bestand-Warnung (Runde 124.9): wenn irgendeine Position mehr will
      // als aktuell auf Lager ist, einmal warnen und Bestätigung verlangen.
      const shortages = clean
        .map((r) => {
          const it = items.find((x) => x.id === r.item_id);
          const stock = Number(it?.stock ?? 0);
          return stock < r.quantity ? { name: it?.name || r.item_id, need: r.quantity, stock } : null;
        })
        .filter(Boolean);
      if (shortages.length > 0 && !confirmedOverdraw) {
        setConfirmedOverdraw(true);
        toast.warning(
          `Bestand reicht nicht für ${shortages.length} Position${shortages.length === 1 ? "" : "en"} ` +
          `(z. B. ${shortages[0].name}: nur ${shortages[0].stock} vorhanden, ${shortages[0].need} nötig). ` +
          `Nochmal auf Speichern klicken zum Bestätigen — sonst geht der Bestand bei Versand ins Minus.`
        );
        setBusy(false);
        return;
      }
      await api.patch(`/delivery-notes/${detail.id}`, { packaging_items: clean });
      toast.success(`${clean.length} Verpackungsposition${clean.length === 1 ? "" : "en"} gespeichert`);
      setConfirmedOverdraw(false);
      if (onSaved) await onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const shipped = detail?.status === "shipped";

  return (
    <div className="mt-4 rounded-2xl border-2 border-[var(--pop-pink)]/30 bg-[var(--pop-pink)]/5 p-3"
         data-testid="packaging-section">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px]">Verpackung ({rows.length})</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={addRow} disabled={shipped}
                  data-testid="btn-add-packaging"
                  className="pop-pill bg-white text-[var(--pop-pink)] text-[10px] font-bold px-2 py-1 hover:bg-[var(--pop-pink)]/10 disabled:opacity-40">
            + Position
          </button>
          <button type="button" onClick={save} disabled={busy || shipped}
                  data-testid="btn-save-packaging"
                  className="pop-pill bg-[var(--pop-pink)] text-white text-[10px] font-bold px-2 py-1 hover:opacity-90 disabled:opacity-40">
            {busy ? "…" : "Speichern"}
          </button>
        </div>
      </div>
      {shipped && (
        <div className="text-[10px] text-[var(--text-muted)] mb-2">
          Sendung bereits versendet — Verpackung wurde als Lagerabgang gebucht.
        </div>
      )}
      {rows.length === 0 && (
        <div className="text-[11px] text-[var(--text-muted)] py-2">
          Kein Verpackungsmaterial erfasst. „+ Position" fügt Palette / Karton / Folie hinzu.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((r, i) => {
          const it = items.find((x) => x.id === r.item_id);
          return (
            <div key={i} className="grid grid-cols-[1fr_80px_auto_auto_28px] gap-2 items-center"
                 data-testid={`packaging-row-${i}`}>
              <Select value={r.item_id || undefined}
                      onValueChange={(v) => {
                        const picked = items.find((x) => x.id === v);
                        const nameLc = (picked?.name || "").toLowerCase();
                        updateRow(i, {
                          item_id: v,
                          is_pallet: nameLc.includes("palette") || nameLc.includes("euro"),
                        });
                      }}>
                <SelectTrigger data-testid={`select-packaging-item-${i}`} className="h-8 text-xs">
                  <SelectValue placeholder="Verpackung wählen…" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((x) => (
                    <SelectItem key={x.id} value={x.id}>
                      {x.name}{x.stock != null ? ` · Bestand ${x.stock}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" step="any" min="0" value={r.quantity}
                     data-testid={`input-packaging-qty-${i}`}
                     className={`h-8 text-xs font-mono text-right ${
                       it && Number(r.quantity) > Number(it.stock || 0)
                         ? "border-red-400 bg-red-50"
                         : ""
                     }`}
                     title={
                       it && Number(r.quantity) > Number(it.stock || 0)
                         ? `Achtung: nur ${it.stock ?? 0} auf Lager`
                         : ""
                     }
                     onChange={(e) => updateRow(i, { quantity: e.target.value })} />
              <label className="text-[10px] flex items-center gap-1 whitespace-nowrap cursor-pointer"
                     title="Als Palettentausch auf Kunden-Palettenkonto führen">
                <input type="checkbox" checked={r.is_pallet}
                       data-testid={`checkbox-packaging-pallet-${i}`}
                       onChange={(e) => updateRow(i, { is_pallet: e.target.checked })} />
                Palettenkonto
              </label>
              <div className="text-[10px] font-mono text-[var(--text-muted)] whitespace-nowrap">
                Bestand: {it?.stock != null ? it.stock : "—"}
              </div>
              <button type="button" onClick={() => removeRow(i)}
                      data-testid={`btn-remove-packaging-${i}`}
                      className="text-red-500 hover:text-red-700">
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
